import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    if (!this.isOpen) {
      this.#releasePageScrollIfSafe();
    }

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.setMenuItemEven();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    if (this.isOpen) {
      this.#releasePageScroll();
    }
  }

  #releasePageScroll() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  #releasePageScrollIfSafe() {
    if (this.isOpen) return;
    if (document.querySelector('dialog[open]')) return;
    if (document.querySelector('.compare-modal.show')) return;

    this.#releasePageScroll();
  }

  /**
   * Hide countdown header while the main menu drawer is open; restore when closed.
   * Skips elements already hidden (e.g. user dismissed via cookie).
   *
   * @param {boolean} hidden
   */
  #setCountdownBoxHidden(hidden) {
    const countdownBox = document.getElementById('countdownBox');
    if (!countdownBox) return;

    if (hidden) {
      if (getComputedStyle(countdownBox).display === 'none') return;

      countdownBox.dataset.hiddenByMenu = 'true';
      countdownBox.classList.add('menu-drawer-hidden');
      return;
    }

    if (countdownBox.dataset.hiddenByMenu !== 'true') return;

    countdownBox.classList.remove('menu-drawer-hidden');
    delete countdownBox.dataset.hiddenByMenu;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    const { details } = this.refs;
    return details.hasAttribute('open') || details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * @param {Element} header
   */
  #closeAccordionItem(header) {
    header.classList.remove('active');
    const content = header.nextElementSibling;

    if (content?.classList.contains('menu-drawer__menu-item-main')) {
      $(content).stop(true, true).slideUp(300);
    }
  }

  /**
   * @param {Element} listItem
   * @returns {Element | null}
   */
  #getExpandableHeaderFromListItem(listItem) {
    const directHeader = listItem.querySelector(':scope > .menu-drawer__menu-item-header');
    if (directHeader) return directHeader;

    return listItem.querySelector(':scope .menu-products > .menu-drawer__menu-item-header');
  }

  /**
   * Close open accordions among sibling `.menu-products` or `.menu-drawer__list-item`.
   *
   * @param {Element} header
   */
  #closeSiblingAccordions(header) {
    const menuProducts = header.closest('.menu-products');

    if (menuProducts?.parentElement?.querySelector(':scope > .menu-products ~ .menu-products')) {
      menuProducts.parentElement.querySelectorAll(':scope > .menu-products').forEach((products) => {
        const siblingHeader = products.querySelector(':scope > .menu-drawer__menu-item-header');
        if (siblingHeader && siblingHeader !== header) {
          this.#closeAccordionItem(siblingHeader);
        }
      });
      return;
    }

    const listItem = header.closest('.menu-drawer__list-item');
    const list = listItem?.parentElement;

    if (!(list instanceof HTMLUListElement) || !listItem) return;

    list.querySelectorAll(':scope > .menu-drawer__list-item').forEach((item) => {
      const siblingHeader = this.#getExpandableHeaderFromListItem(item);
      if (siblingHeader && siblingHeader !== header) {
        this.#closeAccordionItem(siblingHeader);
      }
    });
  }

  setMenuItemEven() {
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const header = event.target.closest('.menu-drawer__menu-item-header');
      if (!header || !this.contains(header)) return;

      if (!header.closest('.menu-products') && !header.closest('.menu-drawer__list-item')) return;

      const content = header.nextElementSibling;
      if (!content?.classList.contains('menu-drawer__menu-item-main')) return;

      const wasActive = header.classList.contains('active');

      this.#closeSiblingAccordions(header);

      const $content = $(content);
      const $header = $(header);

      if (wasActive) {
        $content.stop(true, true).slideUp(300);
        $header.removeClass('active');
      } else {
        $content.stop(true, true).slideDown(300);
        $header.addClass('active');
      }
    });
  }

  /**
   * Toggle the main menu drawer. When closing, prevent native <details> toggle so it
   * does not re-open after we remove the open attribute in close().
   *
   * @param {Event} [event]
   */
  toggle(event) {
    if (this.isOpen) {
      event?.preventDefault();
      event?.stopPropagation();
      return this.close();
    }

    return this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    if (details === this.refs.details) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.#setCountdownBoxHidden(true);
    }

    document.querySelector('.announcement-bar__slider')?.classList.add('hide');
    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    document.querySelector('.announcement-bar__slider')?.classList.remove('hide');
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      details.removeAttribute('open');
      this.#releasePageScroll();
      this.#setCountdownBoxHidden(false);
      removeTrapFocus();
    }

    onAnimationEnd(details, () => {
      if (details.classList.contains('menu-open')) return;

      reset(details);

      if (details === this.refs.details) {
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
